import { useCallback, useMemo, useState } from "react";
import { GridExpandMoreIcon } from "@mui/x-data-grid-pro";
import {
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { DialogComponent } from "src/utils/GlobalModal/DialogComponent";
import { dynamicNotification } from "src/utils/Notifications";
import __ from "src/utils/Translations";
import { useSelectedRows } from "src/Pages/Company/Pending/PendingBenefits/SelectedRowsProvider/hooks";
import {
  conditionTypesActionOptions,
  salaryDeductionActionOption,
} from "src/Pages/Company/Pending/PendingBenefits/const";
import { useMassAccept } from "src/Pages/Company/Pending/PendingBenefits/query";

interface Option {
  label: string;
  value: string | string[];
}

export const MassActionsMenu = () => {
  const [dialog, setDialog] = useState(false);
  const [selected, setSelected] = useState<Option[]>([]);

  const handleSelectCondition = useCallback(
    (option: Option) => () => {
      setSelected((prev) => {
        if (prev.map((item) => item.value).includes(option.value)) {
          return prev.filter((item) => item.value !== option.value);
        }
        return [...prev, option];
      });
    },
    []
  );

  const {
    filteredAcceptableWaitingRoomsCount,
    getFilteredMassActionData,
    resetSelection,
    selectedAcceptableSalaryDeductionsCount,
    selectedAcceptableWaitingRoomsCount,
  } = useSelectedRows();

  const handleClose = useCallback(() => {
    setDialog(false);
    setSelected([]);
  }, []);

  const { isPending, mutate } = useMassAccept();

  const confirm = useCallback(() => {
    const selectedValues = selected.map((item) => item.value);
    const payload = getFilteredMassActionData(
      selectedValues.includes(salaryDeductionActionOption.value),
      selectedValues
        .filter((item) => item !== salaryDeductionActionOption.value)
        .flat() || []
    );
    if (payload.documents.length > 0 || payload.waitingRooms.length > 0) {
      mutate(payload, {
        onSuccess: () => {
          resetSelection();
          dynamicNotification(__("Pomyślnie zaakceptowano"));
        },
        onError: () => {
          dynamicNotification(__("Nie udało się zaakceptować"), "error");
        },
        onSettled: () => {
          handleClose();
        },
      });
    } else {
      dynamicNotification(__("Brak warunków do akceptacji"), "warning");
    }
  }, [
    getFilteredMassActionData,
    handleClose,
    mutate,
    resetSelection,
    selected,
  ]);

  const listConditionsToAccept = useMemo(() => {
    const options = [];
    if (selectedAcceptableSalaryDeductionsCount) {
      options.push({
        ...salaryDeductionActionOption,
        count: selectedAcceptableSalaryDeductionsCount,
      });
    }
    if (selectedAcceptableWaitingRoomsCount) {
      conditionTypesActionOptions.forEach((option) => {
        const count = filteredAcceptableWaitingRoomsCount(option.value);
        if (count > 0) {
          options.push({
            ...option,
            count,
          });
        }
      });
    }

    return options;
  }, [
    filteredAcceptableWaitingRoomsCount,
    selectedAcceptableSalaryDeductionsCount,
    selectedAcceptableWaitingRoomsCount,
  ]);

  return (
    <>
      <Button
        color="info"
        size="small"
        variant="outlined"
        endIcon={<GridExpandMoreIcon />}
        disabled={
          selectedAcceptableSalaryDeductionsCount === 0 &&
          selectedAcceptableWaitingRoomsCount === 0
        }
        onClick={() => setDialog(true)}
      >
        {__("Akcje masowe")}
      </Button>
      <DialogComponent
        isOpen={dialog}
        onClose={handleClose}
        title={__("Wykonaj akcje masowe")}
        actions={({ close }) => (
          <>
            <Button
              variant="outlined"
              color="inherit"
              disabled={isPending}
              onClick={close}
            >
              {__("Anuluj")}
            </Button>
            <Button
              variant="contained"
              color="success"
              disabled={selected.length === 0}
              loading={isPending}
              loadingPosition="start"
              onClick={confirm}
            >
              {__("Wykonaj")}
            </Button>
          </>
        )}
      >
        <Typography sx={{ mt: 1 }} component="div">
          {__("Wybierz akcje do wykonania")}
        </Typography>
        <List dense sx={{ width: "100%", bgcolor: "background.paper" }}>
          {listConditionsToAccept.map((option) => (
            <ListItem key={option.label} disablePadding>
              <ListItemButton
                role={undefined}
                onClick={handleSelectCondition(option)}
                dense
                disabled={isPending}
              >
                <ListItemIcon sx={{ minWidth: 0 }}>
                  <Checkbox
                    edge="start"
                    checked={selected.includes(option)}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText
                  primary={`${__(option.label)}: ${option.count}`}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </DialogComponent>
    </>
  );
};
